// On-duty live position reporting.
//
// Additive layer: while a guard has an open time entry (clocked in) and has
// granted live-location consent, positions flow through the existing
// `useLiveTracking` pipeline into `officer_live_locations`, tagged with the
// site of the punch so site-scoped supervisors can see them. Patrol tracking is
// untouched — this only fills the gap where a guard is clocked in but not on a
// patrol. On clock-out the officer's live row is marked offline so it drops off
// the live map instead of lingering as a stale pin.
//
// Native handoff: inside the MissionReady native shell the native DutyLocation
// layer owns reporting (it alone survives backgrounding and a locked screen),
// and the web watcher below is disabled so the same officer is never reported
// twice. Everywhere else the web tracker behaves exactly as before.
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useLiveTracking } from "@/lib/useLiveTracking";
import { useLiveLocationConsent } from "@/lib/live-location-consent";
import { nativeGpsRequiredHere, useRequireNativeLiveGps } from "@/lib/native-gps-policy";
import {
  hasNativeDutyLocation,
  requestNativeDutyPermissions,
  startNativeDutyTracking,
  stopNativeDutyTracking,
  updateNativeDutyAuth,
} from "@/lib/native-duty-location";

export function OnDutyLocationReporter() {
  const { user, currentOrg } = useAuth();
  const [consent] = useLiveLocationConsent(user?.id ?? null);
  const [entry, setEntry] = useState<{ id: string; site_id: string | null; shift_id: string | null } | null>(null);
  const wasActiveRef = useRef(false);

  const userId = user?.id ?? "";
  const orgId = currentOrg?.id ?? "";

  const load = useCallback(async () => {
    if (!userId || !orgId) { setEntry(null); return; }
    const { data } = await supabase
      .from("time_entries")
      .select("id,site_id,shift_id")
      .eq("officer_id", userId)
      .eq("organization_id", orgId)
      .is("clock_out_at", null)
      .order("clock_in_at", { ascending: false })
      .limit(1);
    const row = (data ?? [])[0] as { id: string; site_id: string | null; shift_id: string | null } | undefined;
    setEntry(row ?? null);
  }, [userId, orgId]);

  useEffect(() => { void load(); }, [load]);

  // Keep in step with punches made anywhere (this device or a supervisor edit).
  useEffect(() => {
    if (!userId) return;
    const ch = supabase
      .channel(`on-duty-loc:${userId}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "time_entries", filter: `officer_id=eq.${userId}` },
        () => { void load(); })
      .subscribe();
    const poll = window.setInterval(() => { void load(); }, 120_000);
    return () => { supabase.removeChannel(ch); window.clearInterval(poll); };
  }, [userId, load]);

  // When the workspace requires the installed app for live GPS, the browser
  // watcher stands down so guards are never shown as reporting from a tab a
  // phone will freeze. The app path is unaffected.
  const requireNativeGps = useRequireNativeLiveGps(orgId);
  const active = !!entry && consent === "granted" && !nativeGpsRequiredHere(requireNativeGps);
  // The native shell reports on duty; only one reporter runs at a time.
  const native = hasNativeDutyLocation();
  useLiveTracking({ enabled: active && !native, siteId: entry?.site_id ?? null });

  // Native lifecycle: start on an open punch, stop the moment it closes.
  const nativeStartedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!native) return;
    if (!active || !entry || !userId || !orgId) {
      if (nativeStartedRef.current) {
        nativeStartedRef.current = null;
        void stopNativeDutyTracking();
      }
      return;
    }
    if (nativeStartedRef.current === entry.id) return;
    nativeStartedRef.current = entry.id;

    void (async () => {
      // Asked in an on-duty moment only — never at install or app launch.
      const perms = await requestNativeDutyPermissions(true);
      if (!perms?.foregroundPermission) return;
      const { data } = await supabase.auth.getSession();
      await startNativeDutyTracking({
        officerId: userId,
        shiftId: entry.shift_id,
        organizationId: orgId,
        siteId: entry.site_id,
        timeEntryId: entry.id,
        // Retained for the iOS path, which still uploads directly.
        supabaseUrl: import.meta.env['VITE_SUPABASE_URL'] as string,
        apiKey: import.meta.env['VITE_SUPABASE_PUBLISHABLE_KEY'] as string,
        accessToken: data.session?.access_token,
      });
    })();
  }, [native, active, entry, userId, orgId]);

  // Keep the native uploader in step with the current punch (shift/site), and
  // refresh the iOS bearer token. The Android service signs its own uploads.
  useEffect(() => {
    if (!native || !active) return;
    const push = async () => {
      const { data } = await supabase.auth.getSession();
      await updateNativeDutyAuth(
        data.session?.access_token ?? "",
        entry?.site_id ?? null,
        entry?.shift_id ?? null,
      );
    };
    void push();
    const { data: sub } = supabase.auth.onAuthStateChange(() => { void push(); });
    const timer = window.setInterval(() => { void push(); }, 10 * 60_000);
    return () => { sub.subscription.unsubscribe(); window.clearInterval(timer); };
  }, [native, active, entry?.site_id, entry?.shift_id]);

  // Mark the live row offline when the shift ends (web path; native does the
  // same from the service so it also applies when the app is closed).
  useEffect(() => {
    if (active) { wasActiveRef.current = true; return; }
    if (!wasActiveRef.current || !userId) return;
    wasActiveRef.current = false;
    if (native) return;
    void supabase
      .from("officer_live_locations")
      .update({ status: "offline" } as never)
      .eq("officer_id", userId);
  }, [active, userId, native]);

  return null;
}
